import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Sparkles, X, Circle } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { Mascot } from "@/components/shared/Mascot";
import { useJourney } from "@/hooks/useJourney";
import { http } from "@/lib/http";
import { Link } from "react-router-dom";

/**
 * Wizard step-by-step animado — abre como bottom sheet, mostra coach falando,
 * porquê, checklist e caminho pra ação. Cada slide entra com fade+slide.
 */
export default function JourneyWizard({ stepId, open, onOpenChange }) {
  const { data: journey, markDone, dismiss } = useJourney();
  const [detail, setDetail] = useState(null);
  const [slide, setSlide] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !stepId) return;
    setSlide(0); setDetail(null); setLoading(true);
    http.get(`/journey/step/${stepId}`)
      .then((r) => setDetail(r.data))
      .finally(() => setLoading(false));
  }, [open, stepId]);

  const slides = detail ? buildSlides(detail, journey) : [];
  const total = slides.length;
  const cur = slides[slide];

  const next = () => setSlide((s) => Math.min(s + 1, total - 1));
  const prev = () => setSlide((s) => Math.max(s - 1, 0));
  const goToRoute = () => onOpenChange(false);
  const markAndClose = () => { markDone.mutate(stepId); onOpenChange(false); };
  const dismissAndClose = () => { dismiss.mutate(stepId); onOpenChange(false); };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="bg-[var(--cream)] border-[var(--line)] max-w-md mx-auto rounded-t-[28px] pb-6" data-testid="journey-wizard">
        <DrawerTitle className="sr-only">Coach do passo</DrawerTitle>
        <div className="px-6 pt-2">
          {/* Progress dots */}
          {total > 1 && (
            <div className="flex gap-1.5 mb-6" data-testid="wizard-dots">
              {slides.map((_, i) => (
                <span key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= slide ? "bg-[var(--coffee)]" : "bg-[var(--line)]"}`} />
              ))}
            </div>
          )}

          {loading || !detail ? (
            <p className="serif text-2xl text-[var(--coffee-muted)] animate-pulse py-16 text-center">O coach tá a caminho…</p>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={slide}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.28, ease: "easeOut" }}
                data-testid={`wizard-slide-${slide}`}
              >
                {cur}
              </motion.div>
            </AnimatePresence>
          )}
        </div>

        {/* Nav footer */}
        {!loading && detail && (
          <div className="mt-6 px-6 flex items-center gap-2" data-testid="wizard-footer">
            {slide > 0 ? (
              <button className="btn-secondary flex-1" onClick={prev} data-testid="wizard-prev-button">voltar</button>
            ) : (
              <button className="btn-ghost flex-1 text-[var(--coffee-muted)]" onClick={() => onOpenChange(false)} data-testid="wizard-close-button">
                <X size={14} /> fechar
              </button>
            )}
            {slide < total - 1 ? (
              <button className="btn-primary flex-1" onClick={next} data-testid="wizard-next-button">
                continuar <ArrowRight size={16} />
              </button>
            ) : (
              <Link
                to={detail.route}
                onClick={goToRoute}
                className="btn-primary flex-1 flex items-center justify-center gap-1"
                data-testid="wizard-cta-button"
              >
                {detail.cta} <ArrowRight size={16} />
              </Link>
            )}
          </div>
        )}

        {/* Secondary actions on last slide */}
        {!loading && detail && slide === total - 1 && !detail.done && (
          <div className="mt-3 px-6 flex items-center justify-center gap-4 text-xs text-[var(--coffee-muted)]">
            <button onClick={markAndClose} data-testid="wizard-mark-done" className="min-h-[44px]">
              já fizemos
            </button>
            <span>·</span>
            <button onClick={dismissAndClose} data-testid="wizard-dismiss" className="min-h-[44px]">
              ocultar por enquanto
            </button>
          </div>
        )}
      </DrawerContent>
    </Drawer>
  );
}

function buildSlides(detail, journey) {
  const slides = [];

  // Slide 1: Coach intro (mascote + open + primeira linha)
  slides.push(
    <div className="text-center" data-testid="wizard-intro">
      <div className="flex justify-center">
        <Mascot pose={detail.coach_pose || "thinking"} size={120} />
      </div>
      <p className="eyebrow mt-4">{journey?.mood_kicker || "coach do casal"}</p>
      <h2 className="serif text-3xl leading-[1.05] mt-2 text-[var(--coffee)]">{detail.title}</h2>
      <p className="hand text-xl text-[var(--coral)] mt-3">{journey?.coach_open || "Vamos juntos."}</p>
      <p className="text-sm text-[var(--coffee-muted)] mt-4 leading-relaxed">{detail.subtitle}</p>
    </div>
  );

  // Slide 2+: Coach intro lines (uma por slide se >1 linha) ou combinado num só
  if (detail.coach_intro?.length) {
    slides.push(
      <div data-testid="wizard-coach-lines">
        <div className="flex items-start gap-3 mb-4">
          <Mascot pose={detail.coach_pose || "thinking"} size={64} className="shrink-0" />
          <div>
            <p className="eyebrow">o coach explica</p>
            <p className="serif text-xl mt-1 leading-tight">Por que esse passo?</p>
          </div>
        </div>
        <ul className="space-y-3 mt-3">
          {detail.coach_intro.map((line, i) => (
            <motion.li
              key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }}
              className="text-sm text-[var(--coffee)] leading-relaxed pl-4 border-l-2 border-[var(--coral)]"
            >
              {line}
            </motion.li>
          ))}
        </ul>
        <p className="text-xs text-[var(--coffee-muted)] mt-6 italic leading-relaxed">
          Por quê existir: {detail.why}
        </p>
      </div>
    );
  }

  // Slide 3: Checklist do passo
  if (detail.checklist?.length) {
    slides.push(
      <div data-testid="wizard-checklist">
        <p className="eyebrow">o que vem</p>
        <h3 className="serif text-2xl mt-1 leading-tight">Vai pedir isso</h3>
        <p className="text-sm text-[var(--coffee-muted)] mt-2">Não precisa ter tudo pronto agora — os campos vão aparecendo.</p>
        <ul className="mt-5 space-y-3">
          {detail.checklist.map((item, i) => (
            <motion.li
              key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}
              className="flex items-start gap-3 py-2 border-b border-[var(--line)] last:border-0"
            >
              <Circle size={14} className="text-[var(--teal)] mt-0.5 shrink-0" />
              <span className="text-sm text-[var(--coffee)]">{item}</span>
            </motion.li>
          ))}
        </ul>
        {detail.example && (
          <p className="mt-5 paper p-4 text-sm text-[var(--coffee)] leading-relaxed italic" data-testid="wizard-example">
            <span className="eyebrow block mb-1 not-italic">exemplo</span>
            {detail.example}
          </p>
        )}
      </div>
    );
  }

  // Slide 4: Final — CTA + já-feito
  slides.push(
    <div className="text-center" data-testid="wizard-final">
      <Sparkles size={28} className="mx-auto text-[var(--coral)]" />
      <p className="eyebrow mt-3">bora?</p>
      <h3 className="serif text-3xl leading-tight mt-2 text-[var(--coffee)]">
        {detail.done ? "Já concluído." : "Tudo pronto pra dar o passo."}
      </h3>
      <p className="hand text-xl text-[var(--coral)] mt-3">
        {detail.done ? "Volte quando quiser reeditar." : "Um clique só."}
      </p>
      <p className="text-sm text-[var(--coffee-muted)] mt-4 leading-relaxed">
        {detail.done
          ? "Esse passo já foi feito. Se quiser ajustar, o botão abaixo abre a tela mesmo assim."
          : "O botão abaixo abre a tela certa — é ali que o passo acontece."}
      </p>
      {detail.done && <p className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[var(--teal)]"><Check size={14} /> concluído</p>}
    </div>
  );

  return slides;
}
